import React, { useState, useEffect, useRef, useCallback } from 'react';
import PostCard from './PostCard';
import './VirtualFeed.css';

const VirtualFeed = ({ posts, onLoadMore, hasMore, loading }) => {
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 10 });
  const [containerHeight, setContainerHeight] = useState(0);
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  
  // Estimate item height (adjust based on your PostCard height)
  const ITEM_HEIGHT = 400; // Approximate height of each post
  const BUFFER_SIZE = 5; // Number of items to render outside viewport
  
  useEffect(() => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.clientHeight);
    }
  }, []);
  
  // Calculate visible range based on scroll position
  const calculateVisibleRange = useCallback(() => {
    if (!containerRef.current) return;
    
    const scrollTop = containerRef.current.scrollTop;
    const containerHeight = containerRef.current.clientHeight;
    
    const start = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER_SIZE);
    const end = Math.min(
      posts.length,
      Math.ceil((scrollTop + containerHeight) / ITEM_HEIGHT) + BUFFER_SIZE
    );
    
    setVisibleRange({ start, end });
  }, [posts.length]);
  
  // Handle scroll events
  const handleScroll = useCallback(() => {
    calculateVisibleRange();
    
    // Check if we need to load more posts
    if (hasMore && !loading && containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 1000) {
        onLoadMore();
      }
    }
  }, [calculateVisibleRange, hasMore, loading, onLoadMore]);
  
  // Set up intersection observer for lazy loading
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Trigger lazy loading for this post
            const postId = entry.target.dataset.postId;
            // You can add lazy loading logic here
          }
        });
      },
      {
        root: containerRef.current,
        rootMargin: '100px',
        threshold: 0.1
      }
    );
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
  
  // Get visible posts
  const visiblePosts = posts.slice(visibleRange.start, visibleRange.end);
  
  return (
    <div 
      ref={containerRef}
      className="virtual-feed-container"
      onScroll={handleScroll}
    >
      {/* Spacer for items above visible range */}
      <div 
        style={{ 
          height: visibleRange.start * ITEM_HEIGHT,
          width: '100%'
        }} 
      />
      
      {/* Render only visible posts */}
      {visiblePosts.map((post, index) => {
        const actualIndex = visibleRange.start + index;
        return (
          <div
            key={post.id}
            data-post-id={post.id}
            className="virtual-feed-item"
            style={{
              height: ITEM_HEIGHT,
              position: 'relative'
            }}
          >
            <PostCard 
              post={post}
              onPostUpdate={(updatedPost) => {
                // Handle post updates
                console.log('Post updated:', updatedPost);
              }}
              onPostDelete={(postId) => {
                // Handle post deletion
                console.log('Post deleted:', postId);
              }}
            />
          </div>
        );
      })}
      
      {/* Spacer for items below visible range */}
      <div 
        style={{ 
          height: (posts.length - visibleRange.end) * ITEM_HEIGHT,
          width: '100%'
        }} 
      />
      
      {/* Loading indicator */}
      {loading && (
        <div className="virtual-feed-loading">
          <div className="loading-spinner">Loading more posts...</div>
        </div>
      )}
    </div>
  );
};

export default VirtualFeed;
